import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import type { AuthUser, LoginResponse } from "@/core/api/auth-api";
import {
  login as apiLogin,
  logout as apiLogout,
  refresh as apiRefresh,
  ssoExchange,
} from "@/core/api/auth-api";
import type { PermissionCode } from "@/core/authz/permissions";
import {
  setAuthTokenProvider,
  setSessionInvalidator,
  setSessionRefresher,
} from "@/core/api/http-client";
import { logoutViaSso, sessionCameFromSso } from "@/core/sso/irwms";

/**
 * Auth context for the WMS frontend — same session model as the RSM portal.
 *
 * Persistence:
 *   localStorage 'wms_token' → JWT access token (raw string)
 *   localStorage 'wms_user'  → JSON-serialized AuthUser
 *
 * Identity is owned by the shared RSM user management tables; credentials
 * are verified by the RSM-compatible `POST /auth/login` endpoint.
 *
 * Dev fallback: when VITE_AUTH_MODE=mock, sign-in succeeds locally without
 * a backend so UI work can continue (never enabled outside development).
 */

const TOKEN_KEY = "wms_token";
const USER_KEY = "wms_user";

const AUTH_MODE = (import.meta.env.VITE_AUTH_MODE as string | undefined) ?? "api";

export interface AuthSession {
  token: string;
  user: AuthUser;
}

interface AuthContextValue {
  session: AuthSession | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True if `code` is in the user's permission list, or the user is super admin. */
  hasPermission: (code: PermissionCode) => boolean;
  /**
   * Sign in with PF Number + password. Throws the raw error on failure —
   * callers map it with `mapLoginError` for user-facing copy.
   */
  loginWithPfNumber: (
    pfNumber: string,
    password: string,
    rememberMe: boolean,
  ) => Promise<AuthSession>;
  /**
   * IRWMS single sign-on: exchange the identity provider's access token for a
   * WMS session (POST /auth/sso). Throws the raw ApiError on refusal.
   */
  loginWithIrwms: (irwmsAccessToken: string) => Promise<AuthSession>;
  /**
   * Sign out: notifies the server (best-effort) and clears the local session.
   * A session opened through IRWMS also ends the identity-provider session
   * (front-channel logout reaches the other applications) and lands on the portal.
   */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readPersistedSession(): AuthSession | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const userRaw = localStorage.getItem(USER_KEY);
    if (!token || !userRaw) return null;
    const user = JSON.parse(userRaw) as AuthUser;
    if (!user?.pfNumber) return null;
    return { token, user };
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

function persistSession(response: LoginResponse): void {
  localStorage.setItem(TOKEN_KEY, response.accessToken);
  localStorage.setItem(USER_KEY, JSON.stringify(response.user));
}

function clearPersistedSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/* The HTTP client attaches the bearer token on every request. */
setAuthTokenProvider(() => localStorage.getItem(TOKEN_KEY) ?? undefined);

/*
 * When the HTTP client sees a 401 it needs to wipe the persisted session
 * before bouncing to /login — otherwise LoginPage reads a stale token from
 * localStorage, thinks the user is still authenticated, and immediately
 * navigates back to the protected route, causing an infinite redirect loop.
 */
setSessionInvalidator(clearPersistedSession);

/*
 * Silent refresh: on a 401 the HTTP client calls this to trade the HttpOnly
 * `wms_refresh` cookie for a new access token. We persist the new token to
 * localStorage so the retried request (and every subsequent one) picks it up
 * via authTokenProvider above. The cached React `session` object stays put —
 * the user and permissions haven't changed, only the bearer token.
 */
setSessionRefresher(async () => {
  const response = await apiRefresh();
  persistSession(response);
});

function mockLoginResponse(pfNumber: string): LoginResponse {
  return {
    accessToken: "dev-mock-token",
    expiresIn: 3600,
    user: {
      id: pfNumber,
      pfNumber,
      name: pfNumber,
      email: "",
      shopId: null,
      designation: "SSE",
      reportsToUserId: null,
      status: "ACTIVE",
      roles: [{ id: "dev", code: "SSE", name: "SSE" }],
      permissions: [],
      isSuperAdmin: false,
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(readPersistedSession);

  const loginWithPfNumber = useCallback(
    async (pfNumber: string, password: string, rememberMe: boolean): Promise<AuthSession> => {
      const response =
        AUTH_MODE === "mock" && import.meta.env.DEV
          ? mockLoginResponse(pfNumber)
          : await apiLogin({ pfNumber, password, rememberMe });

      persistSession(response);
      const next: AuthSession = { token: response.accessToken, user: response.user };
      setSession(next);
      return next;
    },
    [],
  );

  const loginWithIrwms = useCallback(async (irwmsAccessToken: string): Promise<AuthSession> => {
    clearPersistedSession(); // never let a stale WMS token shadow the exchange
    const response = await ssoExchange(irwmsAccessToken);
    persistSession(response);
    const next: AuthSession = { token: response.accessToken, user: response.user };
    setSession(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    const viaSso = sessionCameFromSso();
    await apiLogout();
    clearPersistedSession();
    setSession(null);
    if (viaSso) {
      logoutViaSso(); // navigates away; nothing after this runs
    }
  }, []);

  const hasPermission = useCallback(
    (code: PermissionCode) =>
      session !== null && (session.user.isSuperAdmin || session.user.permissions.includes(code)),
    [session],
  );

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      isAuthenticated: session !== null,
      hasPermission,
      loginWithPfNumber,
      loginWithIrwms,
      logout,
    }),
    [session, hasPermission, loginWithPfNumber, loginWithIrwms, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
